'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import Link from 'next/link'
import { styled } from 'styled-components'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  PERSONALIZATION_SESSION_KEY,
  usePersonalizationEligibility,
} from '@b-stock/fe-core/bstock-next/hooks'
import {
  NestableAnalyticsProvider,
  noop,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import { cardViewAllLinkStyles } from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import useAccount from '@/buyer-portal/helpers/useAccount'
import Carousel, {
  CarouselItemWrapper,
} from '@/components/common/carousel/Carousel'
import { FeatureFlags } from '@/helpers/featureFlags'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import listingSearchQuery from '@/queries/listingSearchQuery'
import type { SearchResultsPage } from '@/queries/listingSearchQuery'
import { viewedListingsQuery } from '@/queries/viewedListingsQuery'

import ListingCard from '../listings/listingCard/ListingCard'
import ListingCardSkeleton from '../listings/listingCard/ListingCardSkeleton'
import { defaultState } from '../listings/search/ListingSearchProvider/defaultState'
import ListingSearchContext from '../listings/search/ListingSearchProvider/ListingSearchContext'
import {
  LISTINGS_SORT_PERSONALIZED,
  LISTINGS_SORT_RECOMMENDED,
} from '../listings/search/ListingSearchProvider/types'
import { listingSearchStateToSearchParams } from '../listings/search/ListingSearchProvider/utils'
import { generateViewMoreUrl } from './generateViewMoreUrl'
import { mapBuyingMerchandiseToCategories } from './mapBuyingMerchandiseToCategories'

const ViewAll = styled(Link)`
  ${cardViewAllLinkStyles}
`

const CarouselWrapper = styled.div`
  container-type: inline-size;
  padding: calc(1rem - 0.125rem) calc(1rem - 0.3125rem);

  ${CarouselItemWrapper} > div {
    display: grid;
    margin-right: 1rem;
  }
`

const CardWrapper = styled.div`
  display: grid;
  min-width: 0;
  width: calc(100cqw - 1rem);

  @container (min-width: 29.5rem) {
    width: calc(50cqw - 1rem);
  }

  @container (min-width: 44.75rem) {
    width: calc(33.333cqw - 1rem);
  }
`

const ITEM_LIST_ID = 'carousel-recommended-for-you-ldp'
const ITEM_LIST_NAME = 'Recommended for You'

export function LdpPersonalizedCarousel({
  excludeListingId,
  listingStorefrontId,
}: {
  excludeListingId: string
  listingStorefrontId: string
}) {
  // All hooks called unconditionally — early returns are deferred to after the
  // last hook call to satisfy the Rules of Hooks.
  const flag = useFlag(FeatureFlags.ENABLE_LDP_RECOMMENDED_FOR_YOU)
  const arm = useFlag(FeatureFlags.PERSONALIZED_CAROUSEL_AB)
  const isRecentlyViewedEnabled = useFlag(
    FeatureFlags.ENABLE_RECENTLY_VIEWED_CAROUSEL
  )
  const eligibility = usePersonalizationEligibility('/home-portal/api/headers')
  const isEligibleTreatment = arm === 'treatment' && eligibility === 'eligible'

  const decoded = useDecodedAccessToken()
  const personId = decoded?.personId
  const { data: account } = useAccount()

  const categories = useMemo(
    () =>
      mapBuyingMerchandiseToCategories(
        account?.business?.buyingMerchandise ?? []
      ),
    [account]
  )

  const searchState = useMemo(() => {
    const [sortBy, sortOrder] = isEligibleTreatment
      ? LISTINGS_SORT_PERSONALIZED
      : LISTINGS_SORT_RECOMMENDED
    return {
      ...defaultState,
      sortBy,
      sortOrder,
      filters: {
        ...defaultState.filters,
        categories,
        listingType: ['SPOT'],
        // GLOB-3465 AC4b: the carousel must not recommend listings from
        // sellers this buyer is ineligible to purchase from. eligibleOnly
        // is the request-level lever for that (see BP-2474) — it isn't
        // gated by ENABLE_ELIGIBLE_ONLY_FILTER, which only controls the
        // buyer-facing toggle on the All Listings page.
        eligibleOnly: true,
      },
    }
  }, [isEligibleTreatment, categories])

  const innerParams = useMemo(
    () => listingSearchStateToSearchParams(searchState),
    [searchState]
  )

  const searchQueryArg = useMemo(
    () => ({
      params: innerParams,
      usePostEndpoint: true as const,
      fetchFilters: false,
    }),
    [innerParams]
  )

  const { data, isLoading } = listingSearchQuery.useWithOpts([searchQueryArg], {
    enabled:
      flag.enabled &&
      !!personId &&
      categories.length > 0 &&
      eligibility !== 'pending',
  })

  const { data: viewedIds = [] } = viewedListingsQuery.useWithOpts(
    [personId ?? ''],
    { enabled: flag.enabled && isRecentlyViewedEnabled && !!personId }
  )

  const filteredListings = useMemo(() => {
    const excludedStorefronts = new Set(flag.excludedStorefrontIds ?? [])
    const excludedIds = new Set([excludeListingId, ...viewedIds])
    return (data?.listings ?? []).filter(
      (listing) =>
        !excludedIds.has(listing.listingId) &&
        !excludedStorefronts.has(listing.storefrontId)
    )
  }, [data, excludeListingId, viewedIds, flag.excludedStorefrontIds])

  useEffect(() => {
    if (isEligibleTreatment && data?.personalizationNotFound) {
      sessionStorage.setItem(PERSONALIZATION_SESSION_KEY, 'false')
    }
  }, [isEligibleTreatment, data?.personalizationNotFound])

  const sortType: 'personalization' | 'recommended' =
    isEligibleTreatment && !!data && !data.personalizationNotFound
      ? 'personalization'
      : 'recommended'

  const { track } = useAnalytics()
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (!isLoading && filteredListings.length > 0) {
      if (impressionTimeoutRef.current)
        clearTimeout(impressionTimeoutRef.current)
      impressionTimeoutRef.current = setTimeout(() => {
        const impressionId = crypto.randomUUID()
        setCurrentImpressionId(impressionId)
        track('listing_card_impression', {
          listing_card_impression_id: impressionId,
          listings_count: filteredListings.length,
          carousel_name: 'recommended-for-you-ldp',
          screen_name: ScreenName.ListingDetails,
          sort_type: sortType,
          listings: filteredListings.map((l, i) => ({
            id: l.id,
            position: i + 1,
          })),
        })
      }, 3000)
    }
    return () => {
      if (impressionTimeoutRef.current)
        clearTimeout(impressionTimeoutRef.current)
    }
  }, [isLoading, filteredListings, track, sortType])

  const accessToken = useAccessToken()
  const queryClient = useQueryClient()

  const updateSearchData = useCallback(
    (updater: (data: SearchResultsPage) => SearchResultsPage) => {
      queryClient.setQueryData<SearchResultsPage>(
        listingSearchQuery.getKey(accessToken, [searchQueryArg]),
        (oldData) => (oldData ? updater(oldData) : undefined)
      )
    },
    [searchQueryArg, accessToken, queryClient]
  )

  const contextValue = useMemo(
    () => ({
      data: data ?? null,
      getActiveFilterCount: () => 0,
      isLoading,
      resetFilters: noop,
      setPage: noop,
      setPageSize: noop,
      setSavedSearch: noop,
      setSort: noop,
      setView: noop,
      setSortFrozen: noop,
      recordBidPlacedWhileFrozen: noop,
      state: { ...defaultState, page: 0, pageSize: 12 },
      updateFilters: noop,
      updateSearchData,
      error: null,
      dispatch: noop,
      currentImpressionId,
      similarSearch: { enabled: false, data: undefined, isFetching: false },
      myBidsFilter: { isLoading: false, isError: false },
      personalizationEligibility: 'pending' as const,
    }),
    [data, isLoading, updateSearchData, currentImpressionId]
  )

  const analyticsData = useMemo(
    () => ({
      carousel_name: 'recommended-for-you-ldp',
      sort_type: sortType,
      ...(currentImpressionId && {
        listing_card_impression_id: currentImpressionId,
      }),
    }),
    [currentImpressionId, sortType]
  )

  if (!flag.enabled) return null
  if (flag.excludedStorefrontIds?.includes(listingStorefrontId)) return null
  if (!personId) return null
  if (categories.length === 0) return null
  if (!isLoading && filteredListings.length === 0) return null

  const viewMoreUrl = generateViewMoreUrl(categories, sortType)

  const handleViewAllClick = () => {
    track('button_click', {
      button_name: 'carousel_view_all',
      carousel_name: 'recommended-for-you-ldp',
      screen_name: ScreenName.ListingDetails,
      sort_type: sortType,
    })
  }

  return (
    <AccordionCard
      hideToggle
      title={
        <FormattedMessage id="Common.Carousel.personalizedCategoryTitle" />
      }
      headerAction={
        <ViewAll href={viewMoreUrl} onClick={handleViewAllClick}>
          <FormattedMessage id="Common.Carousel.viewAll" />
          <FontAwesomeIcon icon={faArrowRight} size="xs" />
        </ViewAll>
      }
    >
      <CarouselWrapper>
        <NestableAnalyticsProvider value={analyticsData}>
          <ItemListProvider
            itemListId={ITEM_LIST_ID}
            itemListName={ITEM_LIST_NAME}
          >
            <ListingSearchContext.Provider value={contextValue}>
              <Carousel minItemsToShowButtons={2}>
                {isLoading && filteredListings.length === 0
                  ? Array.from({ length: 4 }, (_, i) => (
                      <CardWrapper key={i}>
                        <ListingCardSkeleton />
                      </CardWrapper>
                    ))
                  : filteredListings.map((listing, index) => (
                      <CardWrapper key={listing.id}>
                        <ListingCard
                          listing={listing}
                          screenName={ScreenName.ListingDetails}
                          position={index + 1}
                        />
                      </CardWrapper>
                    ))}
              </Carousel>
            </ListingSearchContext.Provider>
          </ItemListProvider>
        </NestableAnalyticsProvider>
      </CarouselWrapper>
    </AccordionCard>
  )
}
